import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import Seo from "@/components/common/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { Loader2, ArrowLeft, Calendar, User } from "lucide-react";

export default function BlogPostPage() {
  const { slug } = useParams();
  const q = useQuery({
    queryKey: ["blog", slug],
    queryFn: async () => {
      const { data } = await sharedApi.get(`/classifieds/blog/${slug}`);
      return data;
    },
    enabled: !!slug,
  });

  if (q.isLoading) {
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-20 text-center">
        <Loader2 className="w-6 h-6 mx-auto animate-spin text-slate-400" />
      </div>
    );
  }
  if (q.isError || !q.data) {
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-20 text-center">
        <h1 className="font-display text-3xl font-bold text-slate-900">Post not found</h1>
        <Link to="/blog" className="mt-6 inline-block rounded-full bg-[color:var(--brand-blue)] text-white font-bold px-5 py-2.5">Back to blog</Link>
      </div>
    );
  }
  const p = q.data;
  return (
    <article className="max-w-3xl mx-auto px-4 md:px-8 py-8 md:py-12">
      <Seo title={p.seo_title || p.title} description={p.seo_description || p.excerpt} image={p.cover_image_url} type="article" />
      <Breadcrumbs items={[{ label: "Blog", to: "/blog" }, { label: p.title }]} />

      <Link to="/blog" className="mt-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[color:var(--brand-blue)] transition-colors">
        <ArrowLeft className="w-4 h-4" /> All posts
      </Link>

      {p.tags?.length > 0 && (
        <div className="mt-6 flex flex-wrap gap-1">
          {p.tags.map((t) => (
            <span key={t} className="text-[10px] font-semibold uppercase tracking-widest text-[color:var(--brand-blue)] bg-[color:var(--brand-blue-wash)] rounded-full px-2 py-0.5">{t}</span>
          ))}
        </div>
      )}

      <h1 className="mt-3 font-display text-3xl md:text-5xl font-black text-slate-900 leading-tight tracking-tight">{p.title}</h1>

      <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-slate-500">
        {p.author_name && <span className="inline-flex items-center gap-1"><User className="w-4 h-4" />{p.author_name}</span>}
        {p.published_at && <span className="inline-flex items-center gap-1"><Calendar className="w-4 h-4" />{new Date(p.published_at).toLocaleDateString("en-NZ", { day: "2-digit", month: "long", year: "numeric" })}</span>}
      </div>

      {p.cover_image_url && (
        <img src={p.cover_image_url} alt={p.title} className="mt-8 w-full rounded-2xl shadow-card" />
      )}

      <div
        className="prose prose-slate max-w-none mt-8 leading-relaxed"
        dangerouslySetInnerHTML={{ __html: p.body || "" }}
      />
    </article>
  );
}
